// src/components/BankLedger.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Alert, Chip, IconButton
} from '@mui/material';
import { ArrowBack, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';
import jsPDF from 'jspdf';
import { fmt, sym } from '../utils/currency';
import autoTable from 'jspdf-autotable'; // ← CORRECT IMPORT

const BankLedger = () => {
  const { accountNo } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [bankName, setBankName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [ledgerRes, banksRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/finance/bank-ledger/${accountNo}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/finance/banks`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
        ]);
        setEntries(ledgerRes.data);
        const bank = banksRes.data.find(b => b.accountNo === accountNo);
        setBankName(bank ? `${bank.bankName} - ${bank.accountName}` : accountNo);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load ledger');
      }
    };
    fetch();
  }, [accountNo]);

  const finalBalance = entries.length > 0 ? entries[entries.length - 1].balance : 0;

  // PDF EXPORT FUNCTION (CORRECT)
  const exportPDF = () => {
    const doc = new jsPDF();
    const title = `Bank Ledger - ${bankName} (${accountNo})`;
    const balance = `Balance: ${sym()} ${fmt(finalBalance)}`;

    doc.setFontSize(16);
    doc.text(title, 14, 22);
    doc.setFontSize(12);
    doc.text(balance, 14, 30);

    const tableData = entries.map(e => [
      new Date(e.date).toLocaleDateString(),
      e.documentNo,
      e.description,
      e.type === 'Debit' ? e.amount.toFixed(2) : '',
      e.type === 'Credit' ? e.amount.toFixed(2) : '',
      e.balance.toFixed(2)
    ]);

    // Use autoTable from import
    autoTable(doc, {
      head: [['Date', 'Document', 'Description', 'Debit', 'Credit', 'Balance']],
      body: tableData,
      startY: 40,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`Bank_Ledger_${accountNo}.pdf`);
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Button startIcon={<ArrowBack />} onClick={() => navigate('/banks')} sx={{ mr: 2 }}>
                Back to Banks
              </Button>
              <Typography variant="h5" color="primary">
                Bank Ledger
              </Typography>
            </Box>

            <IconButton
              color="error"
              onClick={exportPDF}
              title="Export to PDF"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}
            >
              <PictureAsPdf />
            </IconButton>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              {bankName} ({accountNo})
            </Typography>
            <Chip
              label={`Balance: ${sym()} ${fmt(finalBalance)}`}
              color={finalBalance >= 0 ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Document No</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Debit</strong></TableCell>
                <TableCell align="right"><strong>Credit</strong></TableCell>
                <TableCell align="right"><strong>Balance</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e, i) => (
                <TableRow key={e.entryNo} hover>
                  <TableCell>{new Date(e.date).toLocaleDateString()}</TableCell>
                  <TableCell>{e.documentNo}</TableCell>
                  <TableCell>{e.description}</TableCell>
                  <TableCell align="right">{e.type === 'Debit' ? e.amount.toFixed(2) : ''}</TableCell>
                  <TableCell align="right">{e.type === 'Credit' ? e.amount.toFixed(2) : ''}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: i === entries.length - 1 ? 'bold' : 'normal' }}>
                    {e.balance.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BankLedger;